import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, filter, Observable, Subscription } from 'rxjs';
import * as uuid from 'uuid';
import { MessageService } from 'primeng/api';

import { AIChatCompletionMessageApiModel } from '../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../api/ai/enums/AI-chat-completion.enum';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { PendoService } from '../../Misc/services/pendo.service';

import { MentionsService } from '../../../Monitor/Core/services/mentions.service';
import { MonitoringRoutingService } from '../../../Monitor/Core/services/monitoring-routing.service';

import { Mention } from '../models/mention';
import { MentionInsightTypes } from '../enums/mention-insight-types.enum';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { PromptModel, PromptResponseTypesEnum } from '../models/prompt.model';
import { InsightCategoryModel, InsightEntryModel } from '../models/insight.model';
import { CustomInsightType } from '@Monitor/Shared/models/custom-insights-type.model';
import { CoverageAnalysisService } from '@Monitor/Shared/services/coverage-analysis.service';


@Injectable()
export class MentionInsightsExplorerService implements OnDestroy {

    mediaType: MediaTypeEnum;
    allPrompts = {};
    abortController;
    aiConversations = [];
    reader: ReadableStreamDefaultReader<Uint8Array>;
    generatedContent: string;
    generatedInsight: string;

    private language: string;
    protected language$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private selectedMention: Mention;
    protected selectedMention$: BehaviorSubject<Mention> = new BehaviorSubject<Mention>(null);
    private selectedPrompts: PromptModel[];
    protected selectedPrompts$: BehaviorSubject<PromptModel[]> = new BehaviorSubject<PromptModel[]>(null);

    private customPromptsSubject: BehaviorSubject<PromptModel[]> = new BehaviorSubject<PromptModel[]>(null);
    public customPrompts$ = this.customPromptsSubject.asObservable();

    subscriptions: Subscription[] = [];

    readonly defaultPrompts: PromptModel[] = [
        { type: MentionInsightTypes.INTELLIGENT_SUMMARY, responseType: PromptResponseTypesEnum.PLAIN_TEXT },
        { type: MentionInsightTypes.CONTEXTUAL_ANALYSIS, responseType: PromptResponseTypesEnum.PLAIN_TEXT, keywords: [] },
        { type: MentionInsightTypes.SENTIMENT_OPINION, responseType: PromptResponseTypesEnum.PLAIN_TEXT, keywords: [] },
        { type: MentionInsightTypes.MENTION_CHARACTERIZATION, responseType: PromptResponseTypesEnum.INTERACTIVE_LIST },
        { type: MentionInsightTypes.LIKELY_AUDIENCE, responseType: PromptResponseTypesEnum.PLAIN_TEXT },
        { type: MentionInsightTypes.BRANDS_MENTIONED, responseType: PromptResponseTypesEnum.INTERACTIVE_LIST },
        { type: MentionInsightTypes.PEOPLE_MENTIONED, responseType: PromptResponseTypesEnum.INTERACTIVE_LIST },
        { type: MentionInsightTypes.PLACES_MENTIONED, responseType: PromptResponseTypesEnum.INTERACTIVE_LIST },
        { type: MentionInsightTypes.KEY_FACTS_AND_FIGURES, responseType: PromptResponseTypesEnum.PLAIN_TEXT },
        { type: MentionInsightTypes.QUOTES, responseType: PromptResponseTypesEnum.PLAIN_TEXT }
    ];
    readonly sessionKey = 'prompt_';
    readonly endFormatJson: string[] = ['','}',']}','"]}','""]}',',""]}','[]}',':[]}','":[]}','"":[]}','"}',
        ']','}]','"}]','""}]',',""}]','{}]',':{}]','":{}]','"":{}]','"]','":""}]','"":""}]'];


    constructor(
        private mentionsService: MentionsService,
        private userStateService: CurrentUserStateService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private configService: ConfigService,
        private monitoringRoutingService: MonitoringRoutingService,
        private pendoService: PendoService,
        private coverageAnalysisService: CoverageAnalysisService
    ) {
        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().subscribe(route => this.allPrompts = []),
            this.mentionsService.getFocusedMention().subscribe(mention => this.setSelectedMention(mention)),
            this.userStateService.getDataReferenceLanguageTag().subscribe(languageTag => {
                this.setLanguage(languageTag || 'en-US');
            }),
            combineLatest([
                this.selectedMention$.pipe(filter(x => !!x)),
                this.coverageAnalysisService.customInsights$
            ]).subscribe(([mention, prompts]) => {

                const mentionId = mention.mentionId ? mention.mentionId : mention?.id;
                const promptKey = this.sessionKey + MentionInsightTypes.CUSTOM + mentionId + this.language;
                let customPrompts = this.allPrompts[promptKey] ?? [];
                const validIds: number[] = [];

                prompts.forEach(item => {
                    if (item.promptType === CustomInsightType.MENTION_INSIGHTS) {
                        validIds.push(item.id);
                        const existingPromptIndex = customPrompts.findIndex(x => x.customInsightId === item.id);
                        if (existingPromptIndex > -1) {
                            customPrompts[existingPromptIndex].name = item.name;
                            customPrompts[existingPromptIndex].promptText = item.prompt;
                            return;
                        }
                        const prompt = {
                            type: MentionInsightTypes.CUSTOM,
                            responseType: PromptResponseTypesEnum.PLAIN_TEXT,
                            customInsightId: item.id,
                            name: item.name,
                            promptText: item.prompt,
                        } as PromptModel;
                        customPrompts.push(prompt);
                    }
                });
                customPrompts = customPrompts.filter((x: PromptModel) => {
                    const customId = x.customInsightId;
                    // Filter out prompts that have been deleted
                    return customId && validIds.includes(customId);
                });

                this.allPrompts[promptKey] = customPrompts;
                this.customPromptsSubject.next(customPrompts);
            })
        );
    }

    setLanguage(language: string, emit: boolean = true) {
        this.language = language;
        if (this.selectedMention) {
            let mentionId = this.selectedMention.mentionId;
            if (!mentionId && this.selectedMention.id) {
                mentionId = this.selectedMention.id;
            }
            this.getCachedPrompt(mentionId);
        }
        if (emit) {
            this.language$.next(this.language);
        }
    }

    generateInsight(prompt: PromptModel, ignoreFetch: boolean = false): void {
        if (!this.selectedMention.body) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.noBody.errorMessage.summary'),
                detail: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.noBody.errorMessage.detail')
            });
            prompt.isGenerating$.next(false);
            return;
        }

        if (this.selectedMention.wordCount <= 100 && prompt.type === MentionInsightTypes.INTELLIGENT_SUMMARY) {
            // for mention body less than 100 words, we don't call ai to generate insights
            prompt.insightText = this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.bodyTooShort');
            prompt.noContentGenerated = true;
            prompt.isGenerating$.next(false);
            return;
        }

        const conversationMediaTypes = [
            MediaTypeEnum.RADIO,
            MediaTypeEnum.TV,
            MediaTypeEnum.PODCAST
        ];
        if (conversationMediaTypes.includes(this.mediaType)
            && prompt.type === MentionInsightTypes.QUOTES
        ) {
            // For quote insight, since the transcript is a conversation,
            // the AI is recognizing it all as quotes and generally will just reformat the mention transcription in full.
            // Thus, we generate default output "No quotes found." as placeholder
            prompt.insightText = this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.quote.broadcast');
            prompt.noContentGenerated = true;
            prompt.isGenerating$.next(false);
            return;
        }

        this.aiConversations = [];
        const baseMessage: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
            message : JSON.stringify({
                format: prompt.type === MentionInsightTypes.CUSTOM || prompt.type === MentionInsightTypes.AD_HOC_CUSTOM ?
                    `${ChatMessageTypeModel.MENTION_INSIGHTS}_CUSTOM_BASE`
                    : `${ChatMessageTypeModel.MENTION_INSIGHTS}_BASE`,
                languageTag: this.language,
                mediaType: this.mediaType
            })
        };
        this.aiConversations.push(baseMessage);

        const insightTypeMessage: any = {
            format: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${prompt.type}`,
            languageTag: this.language,
            mediaType: this.mediaType
        };
        if (prompt.type === MentionInsightTypes.CONTEXTUAL_ANALYSIS || prompt.type === MentionInsightTypes.SENTIMENT_OPINION) {
            insightTypeMessage.keywords = prompt.keywords;
            if (prompt.type === MentionInsightTypes.SENTIMENT_OPINION && prompt.keywords.length) {
                insightTypeMessage.format = `${ChatMessageTypeModel.MENTION_INSIGHTS}_${MentionInsightTypes.SENTIMENT_OPINION_WITH_KEYWORDS}`;
            }
        }

        if (prompt.type === MentionInsightTypes.CUSTOM) {
            insightTypeMessage.customPromptId = prompt.customInsightId;
        }

        const insightMessage: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
            message : JSON.stringify(insightTypeMessage)
        };
        this.aiConversations.push(insightMessage);

        if (prompt.type === MentionInsightTypes.QUOTES) {
            const quotesAttributionMessage: any = {
                format: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${MentionInsightTypes.QUOTES_ATTRIBUTION}`,
                languageTag: this.language,
                mediaType: this.mediaType
            };

            const additionalMessage: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
                message : JSON.stringify(quotesAttributionMessage)
            };

            this.aiConversations.push(additionalMessage);
        }

        if (!ignoreFetch) {
            this.fetchData(this.configService.getConfig().apiUrl + '/agents/mention-insights', prompt);
            try {
                this.pendoService.trackEvent({
                    type: 'INSIGHT_GENERATED',
                    data: {
                        insightType: prompt.type
                    }
                });
            } catch (err) {
                // Don't let an error here break things
                console.error(err);
            }
        }
    }

    generateEntryInsight(
        categoryName: string,
        entry: InsightEntryModel,
        prompt: PromptModel
    ) {
        if (!categoryName) {
            return;
        }

        this.generateInsight(prompt, true);

        const aiResponse: AIChatCompletionMessageApiModel = {
            role: ChatRole.AI_RESPONSE,
            chatMessageType: ChatMessageTypeModel.BASIC,
            message : JSON.stringify(prompt.aiResponse)
        };
        this.aiConversations.push(aiResponse);

        this.generatedContent = '';

        const insightTypeMessage: any = {
            format: `${ChatMessageTypeModel.MENTION_INSIGHTS}_${entry.type}`,
            languageTag: this.language,
            mediaType: this.selectedMention.mediaType.id
        };

        if (entry.type === MentionInsightTypes.BRANDS_PEOPLE_PLACES_MENTIONED_FURTHER) {
            insightTypeMessage.name = entry.name;
            insightTypeMessage.sentiment = categoryName;
        }
        if (entry.type === MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_EMOTION) {
            insightTypeMessage.name = categoryName;
            insightTypeMessage.emotion = entry.name;
        }
        if (entry.type === MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_WORD) {
            insightTypeMessage.name = categoryName;
            insightTypeMessage.contextWord = entry.name;
        }

        const insightMessage: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.MENTION_INSIGHTS,
            message : JSON.stringify(insightTypeMessage)
        };

        this.aiConversations.push(insightMessage);
        this.fetchData(this.configService.getConfig().apiUrl + '/agents/mention-insights', entry);
    }

    fetchData(
        url: string,
        prompt: any,
        insightType?: MentionInsightTypes,
        aiReferenceId?: string,
    ) {
        const uint8array = new TextDecoder();

        if (!aiReferenceId) {
            aiReferenceId = uuid.v4();
        }

        this.abortController = new AbortController();

        fetch(url, {
            'headers': {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Accept': 'text/plain, application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'content-type': 'application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Ai-Reference-Id': aiReferenceId
            },
            'body': JSON.stringify(this.getRequestModel(insightType ?? prompt.type)),
            'method': 'POST',
            'mode': 'cors',
            'credentials': 'include',
            signal: this.abortController.signal
        }).then(
            (response) => {
                if (response.status !== 200 && response.status !== 204) {
                    // should give us some context as to why the call fails (at least for now)
                    console.log('response', response);
                    throw new Error;
                }
                prompt.insightText = '';
                this.generatedContent = '';
                this.reader = response.body.getReader();
                const pump = (controller) => {
                    return this.reader.read().then(({ done, value }) => {
                        let nextValue = uint8array.decode(value);

                        if (this.generatedContent === '') {
                            if (nextValue.trim() === '.') {
                                // Handle the case of some amount of whitespace around a .
                                nextValue = '';
                            } else {
                                // handle the case of starting white space but otherwise good content
                                nextValue = nextValue.trimStart();

                                // handle OpenAI bug that returns undesired fence code block wrapper
                                if (nextValue.startsWith('```json')) {
                                    nextValue = nextValue.replace('```json', '');
                                }

                                if (nextValue.startsWith('json')) {
                                    nextValue = nextValue.replace('json', '');
                                }

                                if (nextValue.startsWith('```html')) {
                                    nextValue = nextValue.replace('```html', '');
                                }

                                if (nextValue.startsWith('html')) {
                                    nextValue = nextValue.replace('html', '');
                                }
                            }
                        }


                        if (nextValue.endsWith('```')) {
                            nextValue = nextValue.replace('```', '');
                        }


                        this.generatedContent += nextValue;
                        this.extractContent(prompt, insightType);

                        // When no more data needs to be consumed, close the stream
                        if (done) {
                            controller.close();
                            this.reader?.cancel();
                            this.onComplete(prompt, insightType);
                            return;
                        }
                        // Enqueue the next data chunk into our target stream
                        controller.enqueue(value);
                        return pump(controller);
                    });
                };
                const startFunction = (controller) => {
                    return pump(controller);
                };
                return new ReadableStream({
                    start: startFunction
                });
            }
        ).catch(error => {
            if (!(error instanceof DOMException)) {
                this.messageService.add(
                    {
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                        detail: this.localizationService.transform('Common.dialog.aiHelper.error.description')
                    }
                );
            }
            prompt.isGenerating$?.next(false);
        });
    }

    extractContent(prompt: any, insightType?: MentionInsightTypes): void {
        let insight = '';
        const type = insightType ?? prompt.type;
        switch (type) {
            case MentionInsightTypes.QUOTES:
                const quotesArray: any = this.setInsightJson(this.generatedContent)?.filter(quote => {
                    if (!quote?.quote || (quote?.quote === '')) {
                        return false;
                    }
                    if (!quote?.attribution || (quote?.attribution === '')) {
                        return false;
                    }
                    return quote?.attribution?.toLowerCase() !== 'unattributed';
                });

                quotesArray?.forEach((quote) => {
                    insight += `<p>"${quote.quote ? quote.quote : ''}"</p>`;
                    insight += `<p style="margin-bottom: 10px"><i>&nbsp;&nbsp;&nbsp;&nbsp;- ${quote.attribution ? quote.attribution : ''}</i></p>`;
                });
                prompt.insightText = insight;
                break;
            case MentionInsightTypes.PEOPLE_MENTIONED:
            case MentionInsightTypes.BRANDS_MENTIONED:
            case MentionInsightTypes.PLACES_MENTIONED:
                insight = '';
                const insightJson: any = this.setInsightJson(this.generatedContent);
                const insightModel = { categories: [] };
                if (insightJson) {
                    Object.keys(insightJson).forEach(key => {
                        if (insightJson[key].length > 0) {
                            const category: InsightCategoryModel = {
                                name: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.' + key),
                                entries: insightJson[key].map((val: string) => {
                                    return {
                                        name: val.trim(),
                                        type: MentionInsightTypes.BRANDS_PEOPLE_PLACES_MENTIONED_FURTHER,
                                        expanded: false
                                    } as InsightEntryModel;
                                }),
                                interactable: true
                            };
                            insightModel.categories.push(category);
                        }
                    });
                }
                prompt.insightModel = insightModel;
                break;
            // these three prompts are insightEntryModel
            case MentionInsightTypes.BRANDS_PEOPLE_PLACES_MENTIONED_FURTHER:
            case MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_EMOTION:
            case MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_WORD:
                prompt.insight = this.generatedContent;
                break;
            case MentionInsightTypes.MENTION_CHARACTERIZATION:
                insight = '';
                const characterizationInsightJson: any = this.setInsightJson(this.generatedContent);
                const characterizationModel = { categories: [] };
                if (characterizationInsightJson) {
                    Object.keys(characterizationInsightJson).forEach(key => {
                        let entries = characterizationInsightJson[key] ?? [];
                        if (typeof(entries) === 'string') {
                            entries = entries.split(', ');
                        }
                        let entryInsightType = MentionInsightTypes.MENTION_CHARACTERIZATION;
                        if (key === 'emotions') {
                            entryInsightType = MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_EMOTION;
                        }
                        if (key === 'usedterms') {
                            entryInsightType = MentionInsightTypes.MENTION_CHARACTERIZATION_FURTHER_WORD;
                        }
                        if (entries.length > 0) {
                            const category: InsightCategoryModel = {
                                name: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.' + key),
                                entries: entries.map((val: string) => {
                                    return {
                                        name: val.trim(),
                                        type: entryInsightType,
                                        expanded: false
                                    } as InsightEntryModel;
                                }),
                                interactable: key === 'emotions' || key === 'usedterms'
                            };
                            characterizationModel.categories.push(category);
                        }
                    });
                }
                prompt.insightModel = characterizationModel;
                break;
            default:
                prompt.insightText = this.generatedContent;
                break;
        }
    };

    setInsightJson(response: string): any {
        let insightJson: any;

        for (const endFormat of this.endFormatJson) {
            if (!insightJson) {
                try {
                    insightJson = JSON.parse(response + endFormat);
                } catch (e) {
                    // do nothing, format still doesn't make sense from the streaming
                }
            }
        }
        return insightJson;
    }

    onComplete(prompt?: PromptModel, insightType?: MentionInsightTypes){
        const type = insightType ?? prompt.type;
        switch (type) {
            case MentionInsightTypes.BRANDS_MENTIONED:
            case MentionInsightTypes.PEOPLE_MENTIONED:
            case MentionInsightTypes.PLACES_MENTIONED:
            case MentionInsightTypes.QUOTES:
                prompt.aiResponse = this.generatedContent;
                // eslint-disable-next-line max-len
                if ((type === MentionInsightTypes.QUOTES && !prompt.insightText) || prompt.insightModel?.categories?.reduce((x, y) => x + y.entries?.length, 0) === 0) {
                    prompt.noContentGenerated = true;
                    prompt.insightModel = null;
                    prompt.insightText = this.localizationService.transform(`Mention.insightsExplorerTab.quickInsights.noContentGenerated.${prompt.type}`);
                }
                prompt.isGenerating$.next(false);
                break;
            case MentionInsightTypes.MENTION_CHARACTERIZATION:
                prompt.aiResponse = this.generatedContent;
                let wordCount: string;
                let minutes: string;
                if (this.selectedMention.wordCount) {
                    // Calculation based on average reading speed of 250 words per minute
                    wordCount = this.selectedMention.wordCount + '';
                    if (this.selectedMention.wordCount < 250) {
                        minutes = '<1 ' +  this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.minute');
                    } else {
                        const numMinutes = Math.round(this.selectedMention.wordCount / 250);

                        if (numMinutes === 1) {
                            minutes = numMinutes + ' ' + this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.minute');
                        } else {
                            minutes = numMinutes + ' ' + this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.minutes');
                        }
                    }
                } else {
                    wordCount = this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.unknown');
                    minutes = this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.unknown');
                }
                prompt.insightModel.categories.push({
                    name: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.wordCount'),
                    entries: [{ name: wordCount }],
                    interactable: false
                });
                prompt.insightModel.categories.push({
                    name: this.localizationService.transform('Mention.insightsExplorerTab.quickInsights.timeToRead'),
                    entries: [{ name: minutes }],
                    interactable: false
                });
                prompt.isGenerating$.next(false);
                break;
            default:
                if (!prompt.insightText) {
                    prompt.noContentGenerated = true;
                    prompt.insightText = this.localizationService.transform(`Mention.insightsExplorerTab.quickInsights.noContentGenerated.${prompt.type}`);
                }
                prompt.isGenerating$.next(false);
                break;
        }
    }

    generateInsightContent(prompt: PromptModel): string {
        switch (prompt.type) {
            case MentionInsightTypes.BRANDS_MENTIONED:
            case MentionInsightTypes.PEOPLE_MENTIONED:
            case MentionInsightTypes.PLACES_MENTIONED:
            case MentionInsightTypes.MENTION_CHARACTERIZATION:
                let content = '';
                if (prompt.insightModel) {
                    prompt.insightModel.categories.forEach(category => {
                        content += `<div><p style="font-weight: 600; margin: 5px 0">${category.name}</p>`;
                        if (category.interactable) {
                            content += '<ul style="margin-top: 0; margin-bottom: 15px; padding-left: 25px; list-style-type: disc">';
                            category.entries.forEach(entry => {
                                content += `<li><div><p style="font-weight: 600; margin: 5px 0">${entry.name}</p>`;
                                if (entry.expanded) {
                                    content += `<p style="margin: 0">${entry.insight}</p>`;
                                }
                                content += '</div></li>';
                            });
                            content += '</ul>';
                        } else {
                            content += `<div style="margin-top: 0; margin-bottom: 15px">${category.entries.map(x => x.name).join(', ')}</div>`;
                        }
                    });
                    content += '</div>';
                }

                return content;
            default:
                return prompt.insightText;
        }
    }

    getRequestModel(aiType?: string) {
        return {
            messages: this.aiConversations,
            mentionId: this.selectedMention.mentionId,
            mercuryId: this.selectedMention.id
        };;
    }

    abortFetching(): void {
        this.abortController?.abort();
    }

    public setSelectedMention(selectedMention: Mention): void {
        this.selectedMention = selectedMention;
        if (this.selectedMention) {
            this.mediaType = selectedMention.mediaType.id;
            if (this.selectedMention) {
                let mentionId = this.selectedMention.mentionId;
                if (!mentionId && this.selectedMention.id) {
                    mentionId = this.selectedMention.id;
                }
                this.getCachedPrompt(mentionId);
            }
        }
        this.selectedMention$.next(this.selectedMention);
    }

    getCachedPrompt(mentionId: string) {
        if (this.allPrompts[this.sessionKey + mentionId + this.language]) {
            this.selectedPrompts = this.allPrompts[this.sessionKey + mentionId + this.language];
        } else {
            this.selectedPrompts = JSON.parse(JSON.stringify(this.defaultPrompts));
            for (const prompt of this.selectedPrompts) {
                prompt.mentionId = mentionId;
            }
            this.allPrompts[this.sessionKey + mentionId + this.language] = this.selectedPrompts;
        }
        this.setFormGroup(mentionId);
        this.selectedPrompts.forEach(prompt => {
            if (!prompt.insightText) {
                prompt.insightText = '';
            }
            if (!prompt.isGenerating$) {
                prompt.isGenerating$ = new BehaviorSubject<boolean>(false);
            } else {
                prompt.isGenerating$.next(false);
            }
        });

        this.selectedPrompts$.next(this.selectedPrompts);
    }

    setFormGroup(mentionId: string) {
        for (const prompt of this.allPrompts[this.sessionKey + mentionId + this.language]) {
            if (prompt.hasOwnProperty('keywords')) {
                const keywordsFormControl: FormControl<string[]> = new FormControl<string[]>([]);
                const keywordsFormGroup: FormGroup = new FormGroup({
                    keywords: keywordsFormControl
                });
                prompt.formGroup = keywordsFormGroup;
                if (prompt.type === MentionInsightTypes.CONTEXTUAL_ANALYSIS) {
                    keywordsFormControl.setValidators(Validators.required);
                }
            }
        }
    }

    getLanguage(): Observable<string> {
        return this.language$;
    }

    getSelectedMention(): Observable<Mention> {
        return this.selectedMention$;
    }

    getSelectedPrompts(): Observable<PromptModel[]> {
        return this.selectedPrompts$;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
